(() => {
  "use strict";

  if (window.wolfWarrantyTestInitialized) {
    return;
  }

  window.wolfWarrantyTestInitialized = true;

  const MULTI_WARRANTY_SKUS = [
    "M001", "M002", "M003", "M004", "M005", "M006", "M007",
    "M008", "M009", "M010", "M011", "M012", "M013", "M014",

    "M023", "M024", "M025", "M026", "M027", "M028", "M029",
    "M030", "M031", "M032", "M033", "M034", "M035", "M036",

    "M045", "M046", "M047", "M048", "M049", "M050", "M051",
    "M052", "M053", "M054", "M055", "M056", "M057"
  ];

  const DECLINE_STORAGE_KEY =
    "wolfWarrantyDeclinedSignature";

  const originalFetch = window.fetch.bind(window);

  let refreshTimer = null;
  let refreshInProgress = false;

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  function getWarrantyRoot() {
    return document.querySelector(
      "[data-warranty-refresh-root]"
    );
  }

  function getWarrantySignature() {
    const root = getWarrantyRoot();

    return root?.dataset.warrantySignature || "";
  }

  function warrantyIsRequired() {
    const root = getWarrantyRoot();

    return root?.dataset.warrantyRequired === "true";
  }

  function isWarrantyItem(item) {
    const properties = item.properties || {};
    const sku = String(item.sku || "").trim();

    return (
      properties["_Warranty plan"] === "Multi" ||
      MULTI_WARRANTY_SKUS.includes(sku)
    );
  }

  function hasValidDecline() {
    if (!warrantyIsRequired()) {
      return false;
    }

    return (
      sessionStorage.getItem(DECLINE_STORAGE_KEY) ===
      getWarrantySignature()
    );
  }

  function saveDecline() {
    const signature = getWarrantySignature();

    if (signature) {
      sessionStorage.setItem(
        DECLINE_STORAGE_KEY,
        signature
      );
    }
  }

  function clearDecline() {
    sessionStorage.removeItem(
      DECLINE_STORAGE_KEY
    );
  }

  async function getCurrentCart() {
    const response = await originalFetch(
      `${getShopifyRoot()}cart.js`,
      {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          Accept: "application/json"
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        "The current cart could not be loaded."
      );
    }

    return response.json();
  }

  async function getFreshWarrantyRoot() {
    const response = await originalFetch(
      window.location.href,
      {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: {
          Accept: "text/html"
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        "The warranty section could not be refreshed."
      );
    }

    const html = await response.text();

    const documentCopy =
      new DOMParser().parseFromString(
        html,
        "text/html"
      );

    return documentCopy.querySelector(
      "[data-warranty-refresh-root]"
    );
  }

  function isCartMutationUrl(input) {
    let rawUrl = "";

    try {
      if (input instanceof Request) {
        rawUrl = input.url;
      } else {
        rawUrl = String(input || "");
      }

      const url = new URL(
        rawUrl,
        window.location.origin
      );

      return /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(
        url.pathname
      );
    } catch (error) {
      return false;
    }
  }

  function warrantyStillMatches(
    warrantyItem,
    freshRoot
  ) {
    if (!freshRoot) {
      return false;
    }

    if (
      freshRoot.dataset.warrantyConfigured !== "true"
    ) {
      return false;
    }

    const properties =
      warrantyItem.properties || {};

    return (
      String(
        properties["_Covered item count"] || ""
      ) ===
        String(
          freshRoot.dataset.coveredCount || ""
        ) &&

      String(
        properties["_Covered value"] || ""
      ) ===
        String(
          freshRoot.dataset.coveredValue || ""
        ) &&

      String(
        properties["_Covered SKUs"] || ""
      ) ===
        String(
          freshRoot.dataset.coveredSkus || ""
        ) &&

      String(
        properties["_Covered cart lines"] || ""
      ) ===
        String(
          freshRoot.dataset.coveredLineKeys || ""
        ) &&

      String(
        properties["_Warranty tier"] || ""
      ) ===
        String(
          freshRoot.dataset.warrantyTier || ""
        )
    );
  }

  async function removeWarranty(warrantyItem) {
    const response = await originalFetch(
      `${getShopifyRoot()}cart/change.js`,
      {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          id: warrantyItem.key,
          quantity: 0
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        "The outdated protection plan could not be removed."
      );
    }
  }

  function getCheckoutControls() {
    return document.querySelectorAll(
      [
        "button[name='checkout']",
        "input[name='checkout']",
        "a[href='/checkout']",
        "a[href$='/checkout']"
      ].join(",")
    );
  }

  function setCheckoutAllowed(allowed) {
    getCheckoutControls().forEach((control) => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
      ) {
        control.disabled = !allowed;
      }

      control.classList.toggle(
        "wolf-warranty-checkout-disabled",
        !allowed
      );

      control.setAttribute(
        "aria-disabled",
        allowed ? "false" : "true"
      );
    });
  }

  function updateWarrantyDecisionUI(cart) {
    const root = getWarrantyRoot();

    if (!root) {
      setCheckoutAllowed(true);
      return;
    }

    const required =
      root.dataset.warrantyRequired === "true";

    if (!required) {
      clearDecline();
      setCheckoutAllowed(true);
      return;
    }

    const warrantyInCart =
      cart.items.some(isWarrantyItem);

    const declined =
      hasValidDecline();

    const heading =
      root.querySelector(
        ".wolf-warranty-test__heading"
      );

    const description =
      root.querySelector(
        ".wolf-warranty-test__description"
      );

    const addedMessage =
      root.querySelector(
        "[data-warranty-test-added]"
      );

    const checkbox =
      root.querySelector(
        "[data-warranty-decline]"
      );

    const declineWrap =
      root.querySelector(
        "[data-warranty-decline-wrap]"
      );

    const plans =
      root.querySelector(
        "[data-warranty-test-options]"
      );

    /*
     * WARRANTY IS ALREADY IN CART
     */
    if (warrantyInCart) {

      if (heading) {
        heading.hidden = true;
      }

      if (description) {
        description.hidden = true;
      }

      if (addedMessage) {
        addedMessage.hidden = false;
      }

      if (checkbox) {
        checkbox.checked = false;
      }

      if (plans) {
        plans.hidden = true;
      }

      if (declineWrap) {
        declineWrap.hidden = true;
      }

      setCheckoutAllowed(true);
      return;
    }

    /*
     * NO WARRANTY IN CART
     */
    if (heading) {
      heading.hidden = false;
    }

    if (description) {
      description.hidden = false;
    }

    if (addedMessage) {
      addedMessage.hidden = true;
    }

    if (declineWrap) {
      declineWrap.hidden = false;
    }

    if (plans) {
      plans.hidden = false;
    }

    if (checkbox) {
      checkbox.checked = declined;
    }

    setCheckoutAllowed(declined);
  }

  async function refreshWarrantyDisplay() {
    if (refreshInProgress) {
      return;
    }

    refreshInProgress = true;

    try {
      const cart =
        await getCurrentCart();

      const freshRoot =
        await getFreshWarrantyRoot();

      if (!freshRoot) {
        setCheckoutAllowed(true);
        return;
      }

      const warrantyItem =
        cart.items.find(isWarrantyItem);

      /*
       * If equipment changes and the existing
       * warranty no longer matches, remove it.
       */
      if (
        warrantyItem &&
        !warrantyStillMatches(
          warrantyItem,
          freshRoot
        )
      ) {
        await removeWarranty(warrantyItem);

        clearDecline();

        window.location.reload();
        return;
      }

      const currentRoot =
        getWarrantyRoot();

      /*
       * If eligible cart contents changed,
       * reset the previous decline choice.
       */
      if (
        currentRoot &&
        currentRoot.dataset.warrantySignature !==
          freshRoot.dataset.warrantySignature
      ) {
        clearDecline();
      }

      if (currentRoot) {
        currentRoot.replaceWith(freshRoot);
      }

      updateWarrantyDecisionUI(cart);

    } catch (error) {
      console.error(
        "Warranty refresh error:",
        error
      );

    } finally {
      refreshInProgress = false;
    }
  }

  function scheduleWarrantyRefresh(
    delay = 300
  ) {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(() => {
      refreshWarrantyDisplay();
    }, delay);
  }

  /*
   * WATCH SHOPIFY FETCH CART UPDATES
   */
  window.fetch = async function (...args) {
    const isCartMutation =
      isCartMutationUrl(args[0]);

    const response =
      await originalFetch(...args);

    if (
      isCartMutation &&
      response.ok
    ) {
      scheduleWarrantyRefresh(250);
    }

    return response;
  };

  /*
   * WATCH SHOPIFY XHR CART UPDATES
   */
  const originalXhrOpen =
    XMLHttpRequest.prototype.open;

  const originalXhrSend =
    XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open =
    function (method, url, ...rest) {
      this._wolfWarrantyCartMutation =
        isCartMutationUrl(url);

      return originalXhrOpen.call(
        this,
        method,
        url,
        ...rest
      );
    };

  XMLHttpRequest.prototype.send =
    function (...args) {
      if (
        this._wolfWarrantyCartMutation
      ) {
        this.addEventListener(
          "loadend",
          () => {
            if (
              this.status >= 200 &&
              this.status < 400
            ) {
              scheduleWarrantyRefresh(250);
            }
          },
          { once: true }
        );
      }

      return originalXhrSend.apply(
        this,
        args
      );
    };

  /*
   * "I DON'T WANT A PROTECTION PLAN"
   */
  document.addEventListener(
    "change",
    (event) => {
      if (
        !(event.target instanceof Element)
      ) {
        return;
      }

      const checkbox =
        event.target.closest(
          "[data-warranty-decline]"
        );

      if (!checkbox) {
        return;
      }

      const error =
        document.querySelector(
          "[data-warranty-required-error]"
        );

      if (checkbox.checked) {
        saveDecline();
        setCheckoutAllowed(true);
      } else {
        clearDecline();
        setCheckoutAllowed(false);
      }

      if (error) {
        error.hidden = true;
      }
    }
  );

  /*
   * CHECKOUT + ADD WARRANTY BUTTONS
   */
  document.addEventListener(
    "click",
    async (event) => {
      if (
        !(event.target instanceof Element)
      ) {
        return;
      }

      /*
       * CHECKOUT
       */
      const checkoutControl =
        event.target.closest(
          [
            "button[name='checkout']",
            "input[name='checkout']",
            "a[href='/checkout']",
            "a[href$='/checkout']"
          ].join(",")
        );

      if (
        checkoutControl &&
        checkoutControl.getAttribute(
          "aria-disabled"
        ) === "true"
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const error =
          document.querySelector(
            "[data-warranty-required-error]"
          );

        if (error) {
          error.hidden = false;

          error.scrollIntoView({
            behavior: "smooth",
            block: "nearest"
          });
        }

        return;
      }

      /*
       * ADD WARRANTY
       */
      const warrantyButton =
        event.target.closest(
          "[data-warranty-test-add]"
        );

      if (!warrantyButton) {
        return;
      }

      event.preventDefault();

      if (warrantyButton.disabled) {
        return;
      }

      const warrantyVariantId =
        Number(
          warrantyButton.dataset
            .warrantyVariantId
        );

      const warrantySku =
        warrantyButton.dataset
          .warrantySku?.trim() || "";

      if (
        !Number.isInteger(
          warrantyVariantId
        ) ||
        warrantyVariantId <= 0
      ) {
        alert(
          "The warranty variant ID has not been configured correctly."
        );

        return;
      }

      const originalButtonText =
        warrantyButton.textContent;

      document
        .querySelectorAll(
          "[data-warranty-test-add]"
        )
        .forEach((button) => {
          button.disabled = true;
        });

      warrantyButton.textContent =
        "Adding protection…";

      try {
        const currentCart =
          await getCurrentCart();

        if (
          currentCart.items.some(
            isWarrantyItem
          )
        ) {
          throw new Error(
            "A protection plan is already in your cart."
          );
        }

        clearDecline();

        const response =
          await originalFetch(
            `${getShopifyRoot()}cart/add.js`,
            {
              method: "POST",
              credentials: "same-origin",
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json"
              },

              body: JSON.stringify({
                items: [
                  {
                    id:
                      warrantyVariantId,

                    quantity: 1,

                    properties: {
                      "_Warranty plan":
                        "Multi",

                      "_Warranty plan SKU":
                        warrantySku,

                      "_Warranty term":
                        warrantyButton.dataset
                          .warrantyTerm || "",

                      "_Warranty tier":
                        warrantyButton.dataset
                          .warrantyTier || "",

                      "_Covered item count":
                        warrantyButton.dataset
                          .coveredCount || "",

                      "_Covered value":
                        warrantyButton.dataset
                          .coveredValue || "",

                      "_Covered products":
                        warrantyButton.dataset
                          .coveredProducts || "",

                      "_Covered SKUs":
                        warrantyButton.dataset
                          .coveredSkus || "",

                      "_Covered cart lines":
                        warrantyButton.dataset
                          .coveredLineKeys || "",

                      "_Not covered online":
                        warrantyButton.dataset
                          .uncoveredProducts || ""
                    }
                  }
                ]
              })
            }
          );

        let result = {};

        try {
          result =
            await response.json();
        } catch (error) {
          console.warn(
            "Warranty response was not JSON:",
            error
          );
        }

        if (!response.ok) {
          throw new Error(
            result.description ||
              result.message ||
              `Shopify returned error ${response.status}.`
          );
        }

        window.location.reload();

      } catch (error) {
        console.error(
          "Warranty error:",
          error
        );

        alert(
          error instanceof Error
            ? error.message
            : "The protection plan could not be added."
        );

        document
          .querySelectorAll(
            "[data-warranty-test-add]"
          )
          .forEach((button) => {
            button.disabled = false;
          });

        warrantyButton.textContent =
          originalButtonText;
      }
    },
    true
  );

  /*
   * EXTRA THEME CART EVENTS
   */
  [
    "cart:update",
    "cart:updated",
    "cart:refresh",
    "shopify:cart:lines-update"
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      () => {
        scheduleWarrantyRefresh(250);
      }
    );
  });

  /*
   * INITIAL LOAD
   */
  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        scheduleWarrantyRefresh(0);
      },
      { once: true }
    );
  } else {
    scheduleWarrantyRefresh(0);
  }
})();